import type { TDateISO } from '../../../date.extensions';
import type { Balance } from '../types';

export type Deactivation = {
  reason: string;
  until: string | null;
};

export type LimitedFlag =
  | 'BOT'
  | 'TEST'
  | 'COOLOFF'
  | 'NJ_LIFETIME_DEPOSIT_LIMIT'
  | 'SELF_EXCLUDED'
  | 'TAC_REQUIRED'
  | 'VERIFYING'
  | 'W2G_REVIEW'
  | 'LUGAS_LIMIT_POPUP';

export type LoginResponse = {
  balance: Balance;
  newcomer: boolean;
  jwt: string;
  lastAcceptedTerms: number;
  lastLogin?: TDateISO;
  limited: LimitedFlag[];
  serverTime: TDateISO;
  deactivation?: Deactivation;
};

export type LoginCredentialsBody = {
  email: string;
  password: string;
  username?: string;
  ssoToken?: string;
  ssoTokenLabel?: string;
};

export type LoginOAuthBody = {
  provider: string;
  code: string;
};

/**
 * Cashier session — the payment-fs (`payfs/payint`) contract.
 *
 * These shapes mirror `DepositCashierSession` / `WithdrawalCashierSession` in
 * `packages/mods/payments-orchestrator/mod/src/services/{deposit,withdrawal}/types.ts`
 * of `rethink-monoverse @ 8f9dd0a138a` — the ReThink host layer this app
 * replicates rather than imports (`docs/integrations/cornucopia.md` → "The
 * orchestrator is host-only — we do NOT import it"). Field names are reproduced
 * exactly so a response can be fed into the IndexedDB channel through the
 * orchestrator's own 1:1 mapping (`utils/sessionDataBalances.ts`).
 *
 * The 2023-era PAM shape of the retired `/v1/api/payment/*-cashier-sessions`
 * endpoints (and the Q18 answer describing it) does not apply; see the
 * supersession note in `cornucopia.md`.
 *
 * `bonuses[]` of the deposit contract is deliberately **not** modeled here:
 * bonus composition stays on our side (CMS round-trip + `preclaimed: true`, a
 * PayTech-agreed decision — see `cornucopia-deposit-bonus.ts`). Its canonical
 * shape lives in `cornucopia-storage-domain.ts` (`CornucopiaBonus`) with the
 * agreement notes attached.
 */
export type CashierSessionBalanceOverride = {
  name: string;
  balance: string;
  withdrawableBalance: string;
  minAmount: string;
  maxAmount: string;
};

/**
 * Mirrors the orchestrator's `PaymentMethodCustomization` (`services/deposit/types.ts`);
 * forwarded verbatim to IndexedDB (IGM-4167).
 */
export type DepositCashierSessionPaymentMethodCustomization = {
  name: string;
  position?: number;
  appearance?: string;
  depositAmountRecommendations?: number[];
  depositAmountSelected?: number;
};

export type DepositCashierSession = {
  totalBalance: string;
  balance: string;
  currency: string;
  minAmount: string;
  maxAmount: string;
  paymentBaseUrl: string;
  paymentSessionId: string;
  externalMerchantId: string;
  /** Optional: the response is an unvalidated cast. */
  paymentMethodCustomizations?: DepositCashierSessionPaymentMethodCustomization[];
};

export type WithdrawalCashierSession = {
  totalBalance: string;
  totalWithdrawableBalance: string;
  totalBonusBalance: string;
  balance: string;
  withdrawableBalance: string;
  minAmount: string;
  maxAmount: string;
  currency: string;
  paymentBaseUrl: string;
  paymentSessionId: string;
  externalMerchantId: string;
  balanceOverrides: CashierSessionBalanceOverride[];
};

/**
 * `GET /v1/payfs/payint/payment/customers/payment-details`.
 *
 * Mirrors the orchestrator's `FTDDetails`. Only the first two fields are read
 * here — they are what distinguishes a first deposit from a repeat one for the
 * GTM funnel — but the rest are declared so the shape is not misread as narrower
 * than it is.
 */
export type CustomerPaymentDetails = {
  /**
   * Optional despite the backend contract declaring it required: the response is
   * an unvalidated cast, so consumers must distinguish "absent" from `false`.
   * `cornucopia-tracking.ts` treats a non-boolean as unknown rather than as "no
   * deposit on record".
   */
  hasFirstDeposit?: boolean;
  firstDepositTransactionId?: string;
  firstDepositAmount?: number;
  lastDepositDate?: string;
  lastDepositAmount?: number;
  lastDepositType?: string;
};

export const PrefKey = {
  FAVOURITE_GAMES: 'FAVOURITE_GAMES',
  SHOW_BALANCE_DISABLED: 'SHOW_BALANCE_DISABLED',
  LANGUAGE: 'LANGUAGE',
  CASINO_ONBOARDING: 'CASINO_ONBOARDING',
  SELECTED_THEME: 'SELECTED_THEME',
} as const;

export type PrefKey = (typeof PrefKey)[keyof typeof PrefKey];

export type UserPreferences = {
  favoriteGames: Set<string>;
  isBalanceHidden: boolean;
  language: string;
  onboardingFlowsShown?: Set<string>;
  selectedTheme?: string;
};

export type TransactionDetails = {
  context: string;
  action: string;
  game?: string;
  bonusId?: string;
  campaignId?: string;
};

export type TransactionBalanceDetail = {
  start: number;
  end: number;
};

export const WalletTransactionType = {
  WINNING: 'WINNING',
  WAGER: 'WAGER',
} as const;

export type WalletTransactionType = (typeof WalletTransactionType)[keyof typeof WalletTransactionType];

export type WalletTransaction = {
  id: string;
  source: string;
  destination: string;
  ref: string;
  uref: string;
  details: TransactionDetails;
  amount: number;
  currency: string;
  state: string;
  type: WalletTransactionType;
  created: string;
  modified: string;
  balance?: TransactionBalanceDetail;
};

export type WalletBlockedAmount = {
  amount: number;
};

export const PaymentType = {
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT',
  DEPOSIT: 'DEPOSIT',
  WITHDRAWAL: 'WITHDRAWAL',
  REFUND: 'REFUND',
  BONUS_WITHDRAWAL: 'BONUS_WITHDRAWAL',
} as const;

export type PaymentType = (typeof PaymentType)[keyof typeof PaymentType];

export const PaymentStatus = {
  INITIATED: 'INITIATED',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;

export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export type PaymentHistoryItem = {
  paymentId: string;
  type: PaymentType;
  status: PaymentStatus;
  initiatedAt: string;
  currency: string;
  amount: number;
  initialBalance: number;
  finalBalance: number;
  referenceId: string;
  transactionId: string;
};

export type UserActivitySummaryAPI = {
  scheduledAt: string;
  currency: string;
  stake: number;
  profit: number;
  balance: number;
  status: number;
};

export type UserActivitySummary = {
  currency: string;
  stake: number;
  profit: number;
  balance: number;
};

export type LimitType = 'loss' | 'spending';

export type LimitsHistory = {
  id: string;
  limitType: LimitType;
  period: LimitTerm;
  updatedValue: number;
  updatedByUser: string;
  activatedAt: string;
  referenceDate: string;
  status: LimitHistoryStatus;
  userId: string;
};

export const LimitTerm = {
  MONTHLY: 'monthly',
  WEEKLY: 'weekly',
  DAILY: 'daily',
} as const;

export type LimitTerm = (typeof LimitTerm)[keyof typeof LimitTerm];

export const LimitHistoryStatus = {
  REQUESTED: 'requested',
  WILL_APPLY: 'willApply',
  APPLIED: 'applied',
} as const;

export type LimitHistoryStatus = (typeof LimitHistoryStatus)[keyof typeof LimitHistoryStatus];

export type UserConfirmType = 'LUGAS_LIMIT_POPUP' | 'SH_LOCATION_CONFIRMATION';
